export type QueueOutcomeStatus = 'done' | 'failed' | 'cancelled';

export interface QueueOutcome<T> {
  item: T;
  status: QueueOutcomeStatus;
  error?: unknown;
}

export interface RunSequentialOptions<T> {
  items: T[];
  process: (item: T, index: number) => Promise<void>;
  /** Checked before every item so a cancel never leaves a stale "Processing". */
  isCancelled?: () => boolean;
  /** Checked before every item; return false to skip a removed item. */
  stillPresent?: (item: T) => boolean;
  onCancelled?: (item: T) => void;
}

/**
 * Strictly sequential batch runner. Exactly one recognition is in flight at any
 * time and a single failure never stops the remaining items.
 */
export async function runSequential<T>(options: RunSequentialOptions<T>): Promise<QueueOutcome<T>[]> {
  const { items, process: processItem, isCancelled, stillPresent, onCancelled } = options;
  const outcomes: QueueOutcome<T>[] = [];

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (isCancelled?.()) {
      onCancelled?.(item);
      outcomes.push({ item, status: 'cancelled' });
      continue;
    }
    if (stillPresent && !stillPresent(item)) {
      outcomes.push({ item, status: 'cancelled' });
      continue;
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      await processItem(item, i);
      outcomes.push({ item, status: 'done' });
    } catch (error) {
      outcomes.push({ item, status: 'failed', error });
    }
  }

  return outcomes;
}
